import type { Composition } from '@medplum/fhirtypes'
import { Assessment } from '@seen/core/models/assessments/assessment'
import {
  type NamedNote,
  type NoteTextReaders,
  noteName,
} from '@seen/core/models/noteName'
import { ParticipantNote } from '@seen/core/models/participantNote'
import { VisitNote } from '@seen/core/models/visitNote'
import { extractTextFromHTML } from '#src/utils/html.ts'
import { extractFirstLineFromContent } from '#src/utils/noteContent.ts'

/* The three shapes a Composition takes on the participant's notes list: an
 * Elation-synced note, an Orbit-native note, and an assessment. */
export type DisplayableNote = NamedNote

/* The browser's readers for note text: `DOMParser` for the markup, and a
 * TipTap render with this package's extension set for JSON content. The
 * server keeps its own pair in `@seen/sst-core/html`; the naming rule they
 * both feed is `noteName` in `@seen/core`. */
export const browserNoteText: NoteTextReaders = {
  firstLine: (content) => {
    const firstLine = extractFirstLineFromContent(content)
    if (!firstLine) {
      return undefined
    }
    return firstLine.length > 500
      ? `${firstLine.substring(0, 500).trim()}…`
      : firstLine
  },
  text: extractTextFromHTML,
}

/* The name the notes list shows for a note. The rule is `noteName` in
 * `@seen/core`, shared with the AI tools' listings and the search tool's hits,
 * so anything naming the same note — an AI proposal's source chip, a tool
 * telling the clinician which draft is unsigned — reads identically to the
 * row a clinician would open. */
export function getNoteName(note: DisplayableNote): string {
  return noteName(note, browserNoteText)
}

/* When the note was signed, or undefined while it is still a draft. Both
 * ParticipantNote and Assessment expose `signedOn`; VisitNote uses
 * `signedDate`. */
export function getNoteSignedOn(note: DisplayableNote): string | undefined {
  if (note instanceof VisitNote) {
    return note.signedDate
  }
  return note.signedOn
}

/* The model the notes list would build for a raw Composition, classified the
 * way that list classifies its rows. Elation notes and assessments each carry a
 * `type` system of their own and are checked first; what remains categorised as
 * a note is Orbit-native. Undefined for a Composition that is none of the three
 * — a care-plan snapshot, say — which the notes list does not show either. */
export function toDisplayableNote(
  composition: Composition
): DisplayableNote | undefined {
  if (VisitNote.isInstance(composition)) {
    return VisitNote.fromFhirResource(composition)
  }
  if (Assessment.isInstance(composition)) {
    return Assessment.fromFhirResource(composition)
  }
  if (ParticipantNote.isInstance(composition)) {
    return ParticipantNote.fromFhirResource(composition)
  }
  return undefined
}
